import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import {
  GENDER,
  INITIAL_DATA,
  LOSE_WEIGHT_FOR,
  NAVIGATION,
  QUERY_KEYS,
  QUIZ_STEPS_HISTORY,
  STORAGE_KEY,
} from '@src/constants';
import {
  IStateContextData,
  QUIZ_STEPS,
} from '@src/interfaces/state.interfaces';

export const useQuizService = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data } = useQuery<IStateContextData>({
    queryKey: [QUERY_KEYS.quizState],
    queryFn: () => {
      const localData = localStorage.getItem(STORAGE_KEY.DATA);
      if (localData) return JSON.parse(localData);
      return INITIAL_DATA;
    },
    initialData: INITIAL_DATA,
    enabled: true,
  });

  const changeState = (newData: Partial<IStateContextData>) => {
    queryClient.setQueryData<IStateContextData>(
      [QUERY_KEYS.quizState],
      (oldData = INITIAL_DATA) => {
        const data = { ...oldData, ...newData };
        localStorage.setItem(STORAGE_KEY.DATA, JSON.stringify(data));
        return data;
      },
    );
  };

  const navigateSteps = (
    stepIndex: number,
    currentData: IStateContextData = data,
  ) => {
    const item = QUIZ_STEPS_HISTORY[stepIndex];

    if (!item) return;

    const isPrev = currentData.stepIndex - stepIndex >= 1;

    const shouldSkipEventDate =
      currentData.lose_weight_for === null ||
      currentData.lose_weight_for === undefined ||
      currentData.lose_weight_for === LOSE_WEIGHT_FOR.NONE;

    if (item.step === QUIZ_STEPS.EVENT_DATE && shouldSkipEventDate)
      stepIndex = isPrev ? stepIndex - 1 : stepIndex + 1;

    changeState({ ...currentData, stepIndex });
  };

  const nextStep = () => {
    if (data.stepIndex === QUIZ_STEPS_HISTORY.length - 1) {
      navigate(NAVIGATION.PAYMENT, { replace: true });
      return;
    }

    navigateSteps(data.stepIndex + 1);
  };
  const prevStep = () => {
    if (data.stepIndex === 0 || pathname !== NAVIGATION.QUIZ) {
      navigate(NAVIGATION.HOME);
      return;
    }

    navigateSteps(data.stepIndex - 1);
  };

  const setQuizData = (
    newData: Partial<IStateContextData>,
    goNextStep: boolean = false,
  ) => {
    if (goNextStep) {
      const updatedData = { ...data, ...newData };
      navigateSteps(updatedData.stepIndex + 1, updatedData);
    } else {
      changeState(newData);
    }
  };

  return {
    ...data,
    nextStep,
    prevStep,
    setQuizData,
    goToStep: (stepIndex: number) => {
      if (!QUIZ_STEPS_HISTORY[stepIndex]) return;
      changeState({ stepIndex });
    },
    getData: () => data,
    currentStep: QUIZ_STEPS_HISTORY[data.stepIndex],
    isMale: data.gender === GENDER.MALE,
  };
};
